import api from './api'
import type { ApiResponse, PaginatedResponse } from '../types'

// ================================================================================
// TESTIMINAL SERVICE — consented patient/survivor/community story testimonials.
// Public submit (PENDING_REVIEW) + public read of PUBLISHED; admin review/CRUD.
// Privacy: anonymous testimonials withhold authorName/location in public responses.
// ================================================================================

export type TestimonialRole =
  | 'PATIENT' | 'SURVIVOR' | 'FAMILY' | 'NIKSHAY_MITRA'
  | 'VOLUNTEER' | 'HEALTHCARE_WORKER' | 'CSR_PARTNER' | 'OTHER'

export type TestimonialStatus = 'DRAFT' | 'PENDING_REVIEW' | 'PUBLISHED' | 'ARCHIVED'

export interface Testimonial {
  id: number
  authorName?: string | null
  authorRole?: TestimonialRole
  location?: string | null
  title: string
  body: string
  imageUrl?: string
  isAnonymous?: boolean
  isFeatured?: boolean
  status: TestimonialStatus
  publishedAt?: string
  adminNotes?: string
  viewCount?: number
  createdAt?: string
  /** Language actually served by the API — the requested one if a translation exists, else "en". */
  matchedLanguage?: string
  /** Languages this testimonial is available in; always includes "en" (the canonical row). */
  availableLanguages?: string[]
}

export interface TestimonialRequest {
  authorName?: string
  authorRole?: TestimonialRole
  location?: string
  title: string
  body: string
  imageUrl?: string
  isAnonymous?: boolean
  consentGiven?: boolean
  isFeatured?: boolean
  status?: TestimonialStatus
  adminNotes?: string
}

export interface TestimonialStats {
  TOTAL: number
  DRAFT: number
  PENDING_REVIEW: number
  PUBLISHED: number
  ARCHIVED: number
}

// ---- public ----

export async function submitTestimonial(req: TestimonialRequest): Promise<Testimonial> {
  const res = await api.post<ApiResponse<Testimonial>>('/testimonials', req)
  return res.data.data
}

export async function listTestimonials(params: {
  role?: string
  q?: string
  page?: number
  size?: number
}): Promise<PaginatedResponse<Testimonial>> {
  const qs = new URLSearchParams()
  if (params.role) qs.set('role', params.role)
  if (params.q) qs.set('q', params.q)
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 12))
  const res = await api.get<ApiResponse<PaginatedResponse<Testimonial>>>(`/testimonials?${qs.toString()}`)
  return res.data.data
}

export async function getTestimonial(id: number): Promise<Testimonial> {
  const res = await api.get<ApiResponse<Testimonial>>(`/testimonials/${id}`)
  return res.data.data
}

// ---- admin ----

export async function listTestimonialsAdmin(params: {
  status?: string
  role?: string
  q?: string
  page?: number
  size?: number
}): Promise<PaginatedResponse<Testimonial>> {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.role) qs.set('role', params.role)
  if (params.q) qs.set('q', params.q)
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 20))
  const res = await api.get<ApiResponse<PaginatedResponse<Testimonial>>>(`/testimonials/admin?${qs.toString()}`)
  return res.data.data
}

export async function getTestimonialStats(): Promise<TestimonialStats> {
  const res = await api.get<ApiResponse<TestimonialStats>>(`/testimonials/stats`)
  return res.data.data
}

export async function createTestimonial(req: TestimonialRequest): Promise<Testimonial> {
  const res = await api.post<ApiResponse<Testimonial>>('/testimonials/admin', req)
  return res.data.data
}

export async function updateTestimonial(id: number, req: TestimonialRequest): Promise<Testimonial> {
  const res = await api.put<ApiResponse<Testimonial>>(`/testimonials/${id}`, req)
  return res.data.data
}

export async function changeTestimonialStatus(id: number, status: TestimonialStatus): Promise<Testimonial> {
  const res = await api.put<ApiResponse<Testimonial>>(`/testimonials/${id}/status?status=${status}`)
  return res.data.data
}

export async function deleteTestimonial(id: number): Promise<void> {
  await api.delete<ApiResponse<void>>(`/testimonials/${id}`)
}

// ---- label maps ----

export const TESTIMONIAL_ROLE_LABELS: Record<TestimonialRole, string> = {
  PATIENT: 'Patient',
  SURVIVOR: 'Survivor',
  FAMILY: 'Family member',
  NIKSHAY_MITRA: 'TB Supporter (Ni-kshay Mitra)',
  VOLUNTEER: 'Volunteer',
  HEALTHCARE_WORKER: 'Healthcare worker',
  CSR_PARTNER: 'CSR partner',
  OTHER: 'Other',
}

export const TESTIMONIAL_STATUS_LABELS: Record<TestimonialStatus, string> = {
  DRAFT: 'Draft',
  PENDING_REVIEW: 'Pending review',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived',
}